import * as React from 'react';
import { useEffect } from 'react';

import { compose } from 'recompose';

import Retracked from 'js/app/retracked';

import { Grid } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import PersonalizedEmbeddedMsg from 'bundles/degree-description/components/modules/personalization/PersonalizedEmbeddedMsg';
import { getCtaActionXddp, getMessagesToDisplay } from 'bundles/degree-description/utils/personalizedMessageCriteria';
import Faqs from 'bundles/design-system/components/Faqs';
import FinancialPageSchemaMarkup from 'bundles/xddp/components/seo/FinancialPageSchemaMarkup';
import Container from 'bundles/xddp/components/shared/Container';
import InfoSidebar from 'bundles/xddp/components/shared/InfoSidebar';
import { useScrollToStickyBar } from 'bundles/xddp/hooks/useScrollToStickyBar';
import type { DegreeSubPageProps } from 'bundles/xddp/types';

type Props = DegreeSubPageProps & {
  showFinancialTab: boolean;
};

const FinancialPage: React.FC<Props> = ({
  pageContent,
  showFinancialTab,
  userAgent,
  personalizedDdpData,
  handlePersonalizedRequestInfoClick,
  isStickyBarPresent,
}) => {
  const {
    tuition,
    financialAid,
    financialAidFaqsCollection,
    additionalAdmissionResources,
    atAGlance,
    programName,
    partnerInformation,
    expressionOfInterestContainer,
    slug,
  } = pageContent;

  useEffect(() => {
    if (!showFinancialTab && slug) {
      window.location.href = `/degrees/${slug}`;
    }
  }, [showFinancialTab, slug]);

  useScrollToStickyBar(isStickyBarPresent);

  const messagesToDisplay = getMessagesToDisplay(
    personalizedDdpData,
    slug,
    programName,
    partnerInformation?.partnerName
  );
  const embeddedAction = getCtaActionXddp(
    messagesToDisplay?.embeddedMessageToDisplay?.ctaActionType,
    expressionOfInterestContainer?.applyButtonUrl ?? expressionOfInterestContainer?.hostedApplicationUrl,
    handlePersonalizedRequestInfoClick
  );

  return (
    <div className="rc-FinancialPage">
      <FinancialPageSchemaMarkup pageContent={pageContent} />
      <Container>
        <Grid container>
          <Grid item md={8} sm={12}>
            <Markdown className="tuition rc-Section" source={tuition} />
            {messagesToDisplay.embeddedMessageToDisplay && (
              <PersonalizedEmbeddedMsg
                personalizedMessage={messagesToDisplay.embeddedMessageToDisplay}
                ctaAction={embeddedAction}
                onXddp
              />
            )}
            <Markdown className="financialAid" source={financialAid} />
            <Faqs faqs={financialAidFaqsCollection?.items} />
            <Markdown className="additionalResources rc-Section" source={additionalAdmissionResources} />
          </Grid>
          <InfoSidebar userAgent={userAgent} atAGlance={atAGlance} />
        </Grid>
      </Container>
    </div>
  );
};

export default compose<Props, Props>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'xddp',
      page: 'financial_page',
    },
  }))
)(FinancialPage);
